import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Divider,
  IconButton,
  Slide,
  Snackbar,
  type SnackbarCloseReason,
  Typography,
} from '@mui/material';

import { useReactiveVar } from '@apollo/client/react';
import { X, XLg } from 'react-bootstrap-icons';

import styled from '@common/themes/styled';

import { activeScenarioVar } from '@/common/cache';
import { useTranslation } from '@/common/i18n';
import ScenarioSelector from '@/components/scenario/ScenarioSelector';
import { useSiteWithSetter } from '@/context/site';
import ActionsChooser from './ActionsChooser';
import GlobalParameters from './GlobalParameters';

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spaces.s050};
  justify-content: space-between;
`;

export default function ScenarioEditor({ handleDrawerClose }: { handleDrawerClose: () => void }) {
  const [site] = useSiteWithSetter();
  const { t } = useTranslation();
  const activeScenario = useReactiveVar(activeScenarioVar);
  const [showCustomScenarioSnackbar, setShowCustomScenarioSnackbar] = useState(false);
  const [suppressSnackbar, setSuppressSnackbar] = useState(true);

  useEffect(() => {
    if (!activeScenario.isUserSelected && !suppressSnackbar) {
      setShowCustomScenarioSnackbar(true);
      // We only want to show the snackbar once per scenario change
      setSuppressSnackbar(true);
    } else if (activeScenario.isUserSelected) {
      // It's ok to show the snackbar again if the user has selected a scenario
      setSuppressSnackbar(false);
    }
  }, [activeScenario.isUserSelected, suppressSnackbar]);

  const handleSnackClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowCustomScenarioSnackbar(false);
  };

  const hasGlobalParameters = site.parameters.length > 0;

  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <Box
      id="scenario-editor"
      data-testid="scenario-editor"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scenario-editor-title"
    >
      <DrawerHeader>
        <Typography
          id="scenario-editor-title"
          ref={titleRef}
          variant="h4"
          component="h2"
          sx={{ m: 1, lineHeight: 1 }}
          tabIndex={-1}
        >
          {t('edit-scenario')}
        </Typography>
        <IconButton
          onClick={handleDrawerClose}
          size="small"
          aria-label={t('close-scenario-editor')}
        >
          <XLg aria-hidden="true" focusable="false" />
        </IconButton>
      </DrawerHeader>
      <Box sx={{ p: 1 }}>
        <Box component="section" aria-label={t('scenario')} sx={{ mb: 2 }}>
          <ScenarioSelector testId="scenario-editor" />
        </Box>
        <Box component="section" aria-label={t('actions')} sx={{ mb: 3 }}>
          {hasGlobalParameters && (
            <Box component="section" aria-label={t('all-settings')} sx={{ mb: 3 }}>
              <Divider />
              <GlobalParameters />
            </Box>
          )}
          <ActionsChooser />
        </Box>
      </Box>
      <Snackbar
        open={showCustomScenarioSnackbar}
        message={t('custom-scenario-selected-message')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={5000}
        onClose={handleSnackClose}
        slots={{ transition: Slide }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackClose}>
            <X size={16} />
          </IconButton>
        }
        sx={{ boxShadow: 3 }}
      />
    </Box>
  );
}
